import React from "react";
import ProgressBar from "../components/ProgressBar";
import TopBar from "../components/TopBar";
import Footerr from "../components/Footerr/Footerr";
import { Shield, Eye, Lock, Users, Mail, AlertCircle } from "lucide-react";

function PrivacyPolicy() {
  return (
    <div className="bg-[#f9f9f7] min-h-screen">
      <ProgressBar />
      <TopBar />

      <div className="py-16 px-4">
        <div className="max-w-5xl mx-auto bg-white border border-[#b99653]/20 shadow-lg rounded-2xl p-10">
          {/* Header */}
          <div className="text-center mb-12">
            <Shield className="w-16 h-16 text-[#b99653] mx-auto mb-4" />
            <h1 className="gold-main inline-block text-4xl font-bold px-6 py-2 rounded-lg text-white shadow-md">
              Privacy Policy
            </h1>
            <p className="text-[#101c4c] text-lg mt-4 font-medium">
              Your privacy matters to us at{" "}
              <strong>Brand.Gold Marketplace</strong>
            </p>
            <p className="text-sm text-[#101c4c]/70 mt-2">
              Last updated: October 2025
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-3 flex items-center">
              <Eye className="w-6 h-6 mr-2 text-[#b99653]" />
              Introduction
            </h2>
            <p className="text-[#101c4c]/90 leading-relaxed mb-3">
              <strong>Brand.Gold Marketplace</strong>, operated by Terces
              Solutions Private Limited, is committed to protecting your
              privacy. This policy explains how we collect, use, and protect
              your data while you browse, purchase, or manage domains through
              our digital marketplace.
            </p>
            <div className="bg-[#fcdb6a]/20 border-l-4 border-[#b99653] p-4 rounded-lg">
              <p className="text-[#101c4c]/90">
                By using our platform, you agree to the terms of this Privacy
                Policy. Please read carefully before engaging in transactions or
                account registration.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-3 flex items-center">
              <Users className="w-6 h-6 mr-2 text-[#b99653]" />
              Information We Collect
            </h2>

            <div className="space-y-4">
              <div className="bg-[#fef9eb] border border-[#b99653]/30 rounded-xl p-5">
                <h3 className="font-semibold text-[#101c4c] mb-2">
                  Personal Information
                </h3>
                <ul className="list-disc pl-6 text-[#101c4c]/90 text-sm space-y-1">
                  <li>Name, email address, and contact details</li>
                  <li>Account credentials and preferences</li>
                  <li>Payment and billing information</li>
                  <li>Communication history</li>
                </ul>
              </div>

              <div className="bg-[#f9f9f9] border border-[#b99653]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#101c4c] mb-2">
                  Usage Information
                </h3>
                <ul className="list-disc pl-6 text-[#101c4c]/90 text-sm space-y-1">
                  <li>Marketplace activity and purchase history</li>
                  <li>Saved domains and browsing behavior</li>
                  <li>Device and browser information</li>
                  <li>Platform interaction timestamps</li>
                </ul>
              </div>

              <div className="bg-[#fff6f6] border border-[#b99653]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#101c4c] mb-2">
                  Technical Information
                </h3>
                <ul className="list-disc pl-6 text-[#101c4c]/90 text-sm space-y-1">
                  <li>IP address and approximate location</li>
                  <li>Cookies and analytical tracking data</li>
                  <li>Security and fraud-prevention logs</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-3 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-[#b99653]" />
              How We Use Your Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-[#101c4c] mb-2">
                  Service Delivery
                </h3>
                <ul className="list-disc pl-6 text-[#101c4c]/80 text-sm">
                  <li>Facilitate domain purchases and transfers</li>
                  <li>Manage user accounts and preferences</li>
                  <li>Provide order confirmations and support</li>
                  <li>Maintain transaction security</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#101c4c] mb-2">
                  Platform Optimization
                </h3>
                <ul className="list-disc pl-6 text-[#101c4c]/80 text-sm">
                  <li>Analyze usage trends to improve UX</li>
                  <li>Develop new marketplace features</li>
                  <li>Monitor domain trends and buyer interest</li>
                  <li>Enhance operational performance</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#101c4c] mb-2">
                  Communication
                </h3>
                <ul className="list-disc pl-6 text-[#101c4c]/80 text-sm">
                  <li>Send service notifications and updates</li>
                  <li>Provide marketing materials (optional)</li>
                  <li>Respond to queries or feedback</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[#101c4c] mb-2">
                  Compliance & Security
                </h3>
                <ul className="list-disc pl-6 text-[#101c4c]/80 text-sm">
                  <li>Protect users against fraud and misuse</li>
                  <li>Comply with data protection regulations</li>
                  <li>Ensure safe marketplace operations</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-3">
              Data Security
            </h2>
            <div className="bg-[#fcdb6a]/20 border border-[#b99653]/20 p-4 rounded-xl">
              <p className="text-[#101c4c]/90">
                We implement encryption, secure payment gateways, and regular
                audits to ensure your data remains protected. While we follow
                industry-leading practices, no system is entirely immune to
                risks, and we advise safe digital behavior.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-gold-gradient rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold text-[#101c4c] mb-3 flex items-center">
              <Mail className="w-6 h-6 mr-2 text-[#b99653]" />
              Contact Us
            </h2>
            <p className="text-[#101c4c]/90 mb-4 font-medium">
              Have questions about your privacy? Reach out to our support team.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-md flex items-center gap-3">
                <Mail className="w-6 h-6 text-[#b99653]" />
                <div>
                  <h4 className="font-semibold text-[#101c4c] text-sm">
                    Email Support
                  </h4>
                  <p className="text-[#101c4c]/80 text-sm">
                    marketplace@getbrand.gold
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow-md flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-[#b99653]" />
                <div>
                  <h4 className="font-semibold text-[#101c4c] text-sm">
                    Phone Support
                  </h4>
                  <p className="text-[#101c4c]/80 text-sm">+91 95814 30505</p>
                </div>
              </div>
            </div>
          </section>

          {/* Notice */}
          <div className="mt-10 bg-[#fffaf2] border border-[#b99653]/30 rounded-xl p-6 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-[#b99653] mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-[#101c4c] mb-1">
                Policy Updates
              </h3>
              <p className="text-[#101c4c]/80 text-sm">
                We may update this Privacy Policy periodically. Any updates will
                be reflected on this page and communicated to registered users.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footerr />
    </div>
  );
}

export default PrivacyPolicy;
